import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { getHomepagePreference, setHomepagePreference } from '../utils/HomepagePreference';

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const { logout, user } = useStreamAuth();
  const { profiles, currentProfile, setActiveProfile, clearCurrentProfile } = useProfile();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [homepagePref, setHomepagePref] = useState('movies');

  // Helper function to get profile avatar with Google profile for main account only
  const getProfileAvatar = profile => {
    // Only use Google profile picture for the main account holder (Owner profile)
    // For all  profiles, use ChillFree profile avatar if we have it
    if (profile?.profile_thumb) {
      return profile.profile_thumb;
    }
    if (profile?.profile_name === 'Owner' && user?.picture) {
      return user.picture;
    }
    return null;
  };

  // Load homepage preference when profile changes
  useEffect(() => {
    if (currentProfile?._id) {
      const preference = getHomepagePreference(currentProfile._id);
      setHomepagePref(preference);
    }
  }, [currentProfile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle profile switching
  const handleProfileSwitch = async profile => {
    const success = await setActiveProfile(profile);
    if (success) {
      setIsOpen(false);
      // No page reload needed - profile context will update automatically
    }
  };

  // Handle manage profiles
  const handleManageProfiles = () => {
    setIsOpen(false);
    navigate('/profiles');
  };

  // Handle switch profiles (clear current and go to selection)
  const handleSwitchProfiles = () => {
    clearCurrentProfile();
    setIsOpen(false);
    // Navigate to profiles page without any query parameters
    navigate('/profiles', { replace: true });
  };

  // Handle payment method navigation
  const handlePaymentMethod = () => {
    setIsOpen(false);
    navigate('/account/payment');
  };

  // Handle logout
  const handleLogout = () => {
    setIsOpen(false);
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Handle homepage preference change
  const handleHomepagePreferenceChange = (preference) => {
    if (currentProfile?._id) {
      setHomepagePreference(currentProfile._id, preference);
      setHomepagePref(preference);
    }
  };

  // Don't render if no current profile
  if (!currentProfile) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 transition-colors rounded-lg hover:bg-white/10"
        aria-label="Profile menu"
      >
        {/* Profile Avatar */}
        <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-gray-600 rounded-full">
          {getProfileAvatar(currentProfile) ? (
            <img
              src={getProfileAvatar(currentProfile)}
              alt={currentProfile.profile_name}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full text-sm font-bold text-white">
              {currentProfile.profile_name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Profile Name */}
        <span className="hidden text-sm font-medium text-white sm:block">
          {currentProfile.profile_name}
        </span>

        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 z-50 w-64 mt-2 border border-gray-700 rounded-lg shadow-xl top-full bg-secondary">
          {/* Current Profile Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 overflow-hidden bg-gray-600 rounded-full">
                {getProfileAvatar(currentProfile) ? (
                  <img
                    src={getProfileAvatar(currentProfile)}
                    alt={currentProfile.profile_name}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-lg font-bold text-white">
                    {currentProfile.profile_name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <div className="font-medium text-white">{currentProfile.profile_name}</div>
                <div className="text-sm text-white/60">Current Profile</div>
              </div>
            </div>
          </div>

          {/* Other Profiles */}
          {profiles.length > 1 && (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium tracking-wide uppercase text-white/60">
                Switch to
              </div>
              {profiles
                .filter(profile => profile._id !== currentProfile._id)
                .map(profile => (
                  <button
                    key={profile._id}
                    onClick={() => handleProfileSwitch(profile)}
                    className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
                  >
                    <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-gray-600 rounded-full">
                      {getProfileAvatar(profile) ? (
                        <img
                          src={getProfileAvatar(profile)}
                          alt={profile.profile_name}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-sm font-bold text-white">
                          {profile.profile_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-white">{profile.profile_name}</span>
                  </button>
                ))}
            </div>
          )}

          {/* Actions */}
          <div className="py-2 border-t border-gray-700">
            {/* Homepage Preference */}
            <div className="px-4 py-3 border-b border-gray-700">
              <div className="mb-2 text-xs font-medium tracking-wide uppercase text-white/60">
                Default Homepage
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="homepage-preference"
                    value="movies"
                    checked={homepagePref === 'movies'}
                    onChange={() => handleHomepagePreferenceChange('movies')}
                    className="w-4 h-4 text-[#2f6eb0] border-gray-600 focus:ring-[#2f6eb0] focus:ring-2"
                  />
                  <span className="text-sm text-white group-hover:text-white/80">Movies</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="homepage-preference"
                    value="series"
                    checked={homepagePref === 'series'}
                    onChange={() => handleHomepagePreferenceChange('series')}
                    className="w-4 h-4 text-[#2f6eb0] border-gray-600 focus:ring-[#2f6eb0] focus:ring-2"
                  />
                  <span className="text-sm text-white group-hover:text-white/80">Series</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="homepage-preference"
                    value="live"
                    checked={homepagePref === 'live'}
                    onChange={() => handleHomepagePreferenceChange('live')}
                    className="w-4 h-4 text-[#2f6eb0] border-gray-600 focus:ring-[#2f6eb0] focus:ring-2"
                  />
                  <span className="text-sm text-white group-hover:text-white/80">Live TV</span>
                </label>
              </div>
            </div>

            <button
              onClick={handlePaymentMethod}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Payment Method</span>
            </button>

            <button
              onClick={handleManageProfiles}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Manage Profiles</span>
            </button>

            <button
              onClick={handleSwitchProfiles}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Switch Profiles</span>
            </button>

            <div className="pt-2 mt-2 border-t border-gray-700">
              <button
                onClick={handleLogout}
                className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
              >
                <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
                <span className="text-sm text-white">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
